import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subscription } from 'rxjs/Subscription';

@Injectable()
export class LoaderService {

    protected loaders = [];

    /**
     * Create a new loader instance.
     *
     * @param {string} name - A unique name for the new loader instance.
     * @returns {LoaderInstance} - The new loader instance.
     * @throws {Error} - When a loader with the given name already exists.
     * @public
     */
    public create(name?: string): LoaderInstance {
        // If no name has been given, we'll return an anonymous loader
        if (!name) {
            return this.createAnonymousLoader();
        }

        if (this.exists(name)) {
            return this.get(name);
        }

        const newLoader = new LoaderInstance(name);

        this.loaders = this.loaders.concat([newLoader]);

        return newLoader;
    }

    /**
     * Create an anonymous loader instance.
     *
     * @return {LoaderInstance}
     */
    public createAnonymousLoader() {
        return new LoaderInstance(null);
    }

    public get(name: string) {
        if (this.exists(name)) {
            return this.loaders.find(loader => loader.name === name);
        }

        return false;
    }

    /**
     * Is it still loading?
     *
     * @param name
     * @returns {any}
     */
    public isLoading(name: string) {
        let loader;

        if (loader = this.get(name))
            return loader.isLoading();

        return false;
    }

    /**
     * Check if the load exists
     *
     * @param name
     * @returns {number}
     */
    public exists(name: string) {
        return this.loaders.filter(loader => loader.name === name).length;
    }

    public destroy(name: string) {
        if (this.exists(name)) {
            this.loaders = this.loaders.filter(loader => loader.name !== name);

            return true;
        }
        return false;
    }

}

export class LoaderInstance {

    public name;

    /**
     * The number of currently loading items.
     * @type {BehaviorSubject<number>}
     * @public
     */
    public noLoaders: BehaviorSubject<number> = new BehaviorSubject(0);

    constructor(name) {
        this.name = name;
    }

    /**
     * Start loading.
     * Increase the number of loading items.
     * @returns {LoaderInstance} this
     * @public
     */
    public start(): LoaderInstance {
        this.noLoaders.next(this.noLoaders.getValue() + 1);
        return this;
    }

    /**
     * Stop loading.
     * Decrease the number of loading items. Prevents negative values.
     * @returns {LoaderInstance} this
     * @public
     */
    public stop(): LoaderInstance {
        if (this.noLoaders.getValue() > 0) {
            this.noLoaders.next(this.noLoaders.getValue() - 1);
        }
        return this;
    }

    /**
     * Check if there are loading items.
     * @returns {boolean} - True if the number of loading items is greater than 0.
     * @public
     */
    public isLoading(): boolean {
        return this.noLoaders.getValue() > 0;
    }

    /**
     * Subscribe to noLoaders.
     * @param {(value: any) => void} next
     * @param {(error: any) => void} error
     * @param {() => void} complete
     * @returns {Subscription}
     * @public
     */
    public subscribe(next?: (value: any) => void, error?: (error: any) => void, complete?: () => void): Subscription {
        return this.noLoaders.subscribe(next, error, complete);
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/support/loading/loader.service.ts